import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { SectionTitle } from "@/components/SectionTitle";
import { LeaderCards, StatsTable } from "@/components/stats/StatsSections";

export default function Stats() {
  const { settings } = useSettings();
  const DIVISIONS = useMemo(() => settings.divisions || [], [settings.divisions]);
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [sortKey, setSortKey] = useState("points");
  const [division, setDivision] = useState("Open");

  useEffect(() => {
    api.get("/players").then((r) => setPlayers(r.data)).catch(() => {});
    api.get("/teams").then((r) => setTeams(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (DIVISIONS.length && !DIVISIONS.includes(division)) setDivision(DIVISIONS[0]);
  }, [DIVISIONS, division]);

  const teamName = (id) => teams.find((t) => t.id === id)?.name || "Free Agent";
  const perGame = (p, key) => (p.games_played > 0 ? (p[key] / p.games_played).toFixed(1) : "0.0");

  const divisionPlayers = useMemo(
    () => players.filter((p) => (teams.find((t) => t.id === p.team_id)?.division || "Open") === division),
    [players, teams, division]
  );

  const sorted = useMemo(
    () => [...divisionPlayers].sort((a, b) => b[sortKey] - a[sortKey]),
    [divisionPlayers, sortKey]
  );

  const leaders = [
    { key: "points", label: "Points" },
    { key: "threes", label: "3-Pointers" },
    { key: "rebounds", label: "Rebounds" },
    { key: "assists", label: "Assists" },
  ];

  const cols = [
    { key: "points", label: "PTS" },
    { key: "threes", label: "3PM" },
    { key: "rebounds", label: "REB" },
    { key: "assists", label: "AST" },
    { key: "steals", label: "STL" },
    { key: "blocks", label: "BLK" },
  ];

  return (
    <div className="max-w-7xl mx-auto px-6 py-16" data-testid="stats-page">
      <SectionTitle overline="The numbers" title="Player Stats" sub="Season leaders and full statistical breakdown, separated by division." />

      <div className="flex flex-wrap gap-2 mb-10">
        {DIVISIONS.map((d) => (
          <button
            key={d}
            data-testid={`stats-division-${d.replace(/\s+/g, "-").toLowerCase()}`}
            onClick={() => setDivision(d)}
            className={`px-5 py-2 text-sm font-bold uppercase tracking-wide transition-all duration-200 ${
              division === d ? "bg-[#FF5722] text-black" : "border border-white/15 text-white/70 hover:text-white"
            }`}
          >
            {d}
          </button>
        ))}
      </div>

      <LeaderCards leaders={leaders} divisionPlayers={divisionPlayers} teamName={teamName} perGame={perGame} />
      <StatsTable cols={cols} sorted={sorted} sortKey={sortKey} setSortKey={setSortKey} teamName={teamName} division={division} />
    </div>
  );
}
